// 模板云同步 —— localStorage 仍是 UI 的即时读写层，云端（print worker KV
// tpl:<accountId>）是跨设备/跨浏览器分区的持久层。
//
// 策略（单人使用为主，从简）：
//   启动: 拉云端 bundle → 与本地按「模板 id + updatedAt 新者胜」合并（并集）
//         → 写回本地 + 推送云端 → 广播 pp-templates-reloaded 让 hooks 重载
//   写入: persistence 每次保存成功后 schedulePush()（防抖 3s）推全量快照
//
// 删除用墓碑表达（deletedTemplates：id → 删除时间），随 bundle 一起上传：
// 没有它，A 设备删掉的模板会被 B 设备的旧快照原样推回来，客户反复删不掉。
// 服务端也会按模板 id 合并（不是整包覆盖），两边都做才挡得住互相覆盖。
//
// 为什么必须有它：飞书侧边栏 iframe 的 localStorage 受浏览器存储分区限制，
// 换设备/换浏览器模板就"丢了"；云同步也是后续全屏编辑器（新标签页 ≠ iframe
// 分区）能共享模板的前提。

import { getBaseUserId, getTenantKey } from '../api/bitable';
import { loadTombstones } from './persistence';
import { STORAGE_KEYS } from '../utils/constants';

const API_BASE = 'https://printpro.xiaomiao.win';
const PUSH_DEBOUNCE_MS = 3000;
/** 与 worker 端 TPL_MAX_BYTES 对齐。 */
const MAX_BYTES = 8 * 1024 * 1024;

interface TplLike {
  id: string;
  updatedAt: number;
  shared?: boolean;
  sharedBy?: string;
}
type Store = Record<string, TplLike[]>;

interface Bundle {
  templates: Store;
  contractTemplates: Store;
  /** 模板 id → 删除时间。 */
  deleted?: Record<string, number>;
}

let accountId: string | null = null;
let tenantKey: string | null = null;
let pushTimer: number | null = null;
let started = false;
/** 本次会话里取消共享的模板 id，下次推送时让服务端在团队 bundle 里打墓碑。 */
const unsharedIds = new Set<string>();

export function getAccountId(): string | null {
  return accountId;
}
export function getTenant(): string | null {
  return tenantKey;
}
/** 取消共享：记下来，随下一次推送告诉服务端。 */
export function markUnshared(id: string): void {
  unsharedIds.add(id);
  schedulePush();
}

/** 只留共享出去的那部分。 */
function sharedOnly(store: Store): Store {
  const out: Store = {};
  for (const [tableId, list] of Object.entries(store)) {
    const kept = list.filter((t) => t.shared === true);
    if (kept.length) out[tableId] = kept;
  }
  return out;
}

async function pushTeam(body: string | null): Promise<void> {
  if (!tenantKey || !accountId) return;
  const snap = snapshot();
  const shared = { templates: sharedOnly(snap.templates), contractTemplates: sharedOnly(snap.contractTemplates) };
  // 什么都没共享、也没有要取消的：一个字节都别发。KV 免费版每天只有 1000 次写，
  // 每次个人推送都顺手写一下 team 键，配额半天就烧光，线上用户存不了模板。
  if (
    !unsharedIds.size &&
    !Object.keys(shared.templates).length &&
    !Object.keys(shared.contractTemplates).length
  ) return;
  const payload = body ?? JSON.stringify({ tenantKey, accountId, bundle: shared, unshared: [...unsharedIds] });
  if (payload.length > MAX_BYTES) return;
  try {
    await fetch(`${API_BASE}/api/templates/team`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
    });
    unsharedIds.clear();
  } catch {
    /* 下次再试 */
  }
}

/**
 * 把团队 bundle 合进本地。
 *
 * 团队墓碑（取消共享 / 删除）只作用于本地那些 shared=true 的**副本**：
 * 取消共享的人自己那份已经是 shared=false，不能连他自己的也删掉。
 * 副本被删时顺手写进本地墓碑，否则他自己的个人云端快照还会把它推回来。
 */
function mergeTeam(local: Bundle, team: Bundle): Bundle {
  const teamDeleted = team.deleted || {};
  const localDeleted = { ...(local.deleted || {}) };
  const apply = (store: Store, incoming: Store): Store => {
    const pruned: Store = {};
    for (const [tableId, list] of Object.entries(store)) {
      pruned[tableId] = list.filter((t) => {
        const dead = t.shared === true && teamDeleted[t.id] && (t.updatedAt || 0) <= teamDeleted[t.id];
        if (dead) localDeleted[t.id] = teamDeleted[t.id];
        return !dead;
      });
    }
    // 团队里的模板进本地时带着 shared=true，界面上才认得出它是团队的
    const marked: Store = {};
    for (const [tableId, list] of Object.entries(incoming)) {
      marked[tableId] = list
        .filter((t) => !(teamDeleted[t.id] && (t.updatedAt || 0) <= teamDeleted[t.id]))
        .map((t) => ({ ...t, shared: true }));
    }
    return mergeStore(pruned, marked, localDeleted);
  };
  return {
    templates: apply(local.templates, team.templates || {}),
    contractTemplates: apply(local.contractTemplates, team.contractTemplates || {}),
    deleted: localDeleted,
  };
}

function readStore(key: string): Store {
  try {
    return JSON.parse(localStorage.getItem(key) || '{}') as Store;
  } catch {
    return {};
  }
}

function snapshot(): Bundle {
  return {
    templates: readStore(STORAGE_KEYS.templates),
    contractTemplates: readStore(STORAGE_KEYS.contractTemplates),
    deleted: loadTombstones(),
  };
}

/** 按模板 id 合并两个 store：并集，updatedAt 新者胜；删除过的不再合并回来。 */
function mergeStore(local: Store, cloud: Store, deleted: Record<string, number>): Store {
  const out: Store = {};
  const tables = new Set([...Object.keys(local), ...Object.keys(cloud)]);
  for (const tableId of tables) {
    const byId = new Map<string, TplLike>();
    for (const t of cloud[tableId] ?? []) byId.set(t.id, t);
    for (const t of local[tableId] ?? []) {
      const prev = byId.get(t.id);
      if (!prev || (t.updatedAt || 0) >= (prev.updatedAt || 0)) byId.set(t.id, t);
    }
    out[tableId] = [...byId.values()]
      // 墓碑之后又编辑过的算「删了又重建」，保留
      .filter((t) => !(deleted[t.id] && (t.updatedAt || 0) <= deleted[t.id]))
      .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  }
  return out;
}

async function pushNow(): Promise<boolean> {
  if (!accountId) {
    // 账号还没解析出来（getBaseUserId 还在飞）。丢掉这次推送等于丢掉这段时间
    // 的所有编辑 —— 稍后重试一次。
    if (started) schedulePush();
    return false;
  }
  const body = JSON.stringify({ accountId, bundle: snapshot() });
  if (body.length > MAX_BYTES) {
    // 超限不推（本地照常），避免反复 413
    console.warn('[cloudSync] 模板数据超 8MB，跳过云同步');
    return false;
  }
  let ok = false;
  try {
    const res = await fetch(`${API_BASE}/api/templates`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body,
    });
    ok = res.ok;
  } catch {
    /* 网络失败静默；下次写入会再试 */
  }
  await pushTeam(null);
  return ok;
}

/**
 * 页面被关掉时把待发的快照立刻发出去。
 *
 * 侧边栏经常是「改完就关」，3 秒防抖还没到就 unload，最后那次编辑永远上不了云。
 * keepalive 让请求在页面销毁后仍能发完。
 */
function flushOnHide(): void {
  if (!accountId || pushTimer === null) return;
  window.clearTimeout(pushTimer);
  pushTimer = null;
  const body = JSON.stringify({ accountId, bundle: snapshot() });
  if (body.length > MAX_BYTES) return;
  try {
    void fetch(`${API_BASE}/api/templates`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch {
    /* 关闭途中失败没有补救机会，静默 */
  }
}

/** 还有没推上云的改动（防抖计时器在走）。 */
export function hasPendingPush(): boolean {
  return pushTimer !== null;
}

/** persistence 保存成功后调：防抖推送全量快照。 */
export function schedulePush(): void {
  if (!started) return;
  if (pushTimer) window.clearTimeout(pushTimer);
  pushTimer = window.setTimeout(() => {
    pushTimer = null;
    void pushNow();
  }, PUSH_DEBOUNCE_MS);
}

/** 立即推送（取消待发的防抖），供打开全屏编辑前确保云端有最新模板。 */
export async function flushPush(): Promise<boolean> {
  if (pushTimer) {
    window.clearTimeout(pushTimer);
    pushTimer = null;
  }
  return pushNow();
}

/**
 * 推送当前表的字段快照（id / 名称 / 类型）。
 *
 * 自动化节点里没有 Base SDK，用户只能按**字段名**把值映射进来；而模板里的
 * 信息网格、签章行绑的是**真实 fieldId**。没有这张对照表，渲染服务就没法把
 * 名称换回 id，那些区块在自动化里会全部渲染成空。
 *
 * 顺带在服务端写下 owner:<tableId>，自动化节点靠它按 tableID 反查账号 ——
 * 用户因此不需要登录、不需要填任何绑定信息就能用。
 */
export async function pushTableMeta(
  tableId: string,
  fields: Array<{ id: string; name: string; type: number }>,
): Promise<void> {
  if (!accountId || !tableId || !fields.length) return;
  try {
    await fetch(`${API_BASE}/api/tablemeta`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        accountId,
        tableId,
        fields: fields.map((f) => ({ id: f.id, name: f.name, type: f.type })),
      }),
    });
  } catch {
    /* 推送失败不影响侧边栏使用；下次进插件会再试 */
  }
}

/** 启动同步：拉云端 → 合并 → 写回本地 → 推送 → 通知 UI 重载。
 *  全屏编辑器（无 SDK）通过 explicitAccountId 传入账号。 */
export async function initCloudSync(explicitAccountId?: string, explicitTenantKey?: string): Promise<void> {
  if (started) return;
  started = true;
  if (explicitAccountId) {
    accountId = explicitAccountId;
    tenantKey = explicitTenantKey || null;
  } else {
    try {
      accountId = await getBaseUserId();
    } catch {
      return; // 拿不到账号（如 iframe 外打开）就只用本地
    }
    try {
      tenantKey = await getTenantKey();
    } catch {
      tenantKey = null; // 拿不到企业标识：团队共享不可用，个人同步照常
    }
  }
  window.addEventListener('pagehide', flushOnHide);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushOnHide();
    // 从全屏编辑器那个标签页回来：把那边刚存的拉下来，不用让用户「刷新一下插件」
    else if (document.visibilityState === 'visible') void pullCloud();
  });
  window.addEventListener('focus', () => void pullCloud());
  await pullCloud(true);
}

let pulling = false;
/** 拉云端（个人 + 团队）合并进本地并通知 UI。firstRun 时顺带把本地推上去。 */
export async function pullCloud(firstRun = false): Promise<void> {
  if (!accountId || pulling) return;
  pulling = true;
  try {
    const res = await fetch(
      `${API_BASE}/api/templates?accountId=${encodeURIComponent(accountId)}`,
    );
    const data = (await res.json()) as { bundle: Bundle | null };
    const cloud = data?.bundle;
    if (cloud && typeof cloud === 'object') {
      const local = snapshot();
      const deleted = { ...(cloud.deleted || {}), ...(local.deleted || {}) };
      const merged: Bundle = {
        templates: mergeStore(local.templates, cloud.templates || {}, deleted),
        contractTemplates: mergeStore(
          local.contractTemplates,
          cloud.contractTemplates || {},
          deleted,
        ),
      };
      try {
        localStorage.setItem(STORAGE_KEYS.deletedTemplates, JSON.stringify(deleted));
      } catch {
        /* 墓碑写不进去不影响主流程 */
      }
      localStorage.setItem(STORAGE_KEYS.templates, JSON.stringify(merged.templates));
      localStorage.setItem(
        STORAGE_KEYS.contractTemplates,
        JSON.stringify(merged.contractTemplates),
      );
      window.dispatchEvent(new CustomEvent('pp-templates-reloaded'));
    }
    // 团队共享的模板：同企业的人共用一份
    if (tenantKey) {
      try {
        const tr = await fetch(`${API_BASE}/api/templates/team?tenant=${encodeURIComponent(tenantKey)}`);
        const td = (await tr.json()) as { bundle: Bundle | null };
        if (td?.bundle && typeof td.bundle === 'object') {
          const merged = mergeTeam(snapshot(), td.bundle);
          try {
            localStorage.setItem(STORAGE_KEYS.deletedTemplates, JSON.stringify(merged.deleted || {}));
          } catch {
            /* 墓碑写不进去不影响主流程 */
          }
          localStorage.setItem(STORAGE_KEYS.templates, JSON.stringify(merged.templates));
          localStorage.setItem(STORAGE_KEYS.contractTemplates, JSON.stringify(merged.contractTemplates));
          window.dispatchEvent(new CustomEvent('pp-templates-reloaded'));
        }
      } catch {
        /* 团队模板拉不到不阻塞 */
      }
    }
    // 无论云端有无数据，都推一次当前快照（首次上云）
    if (firstRun) void pushNow();
  } catch {
    /* 拉取失败不阻塞插件使用 */
  } finally {
    pulling = false;
  }
}
